import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Se este e-mail existe, enviamos um link de redefinição.");
    } catch (err) {
      // Backend always returns 200; only network/server issues arrive here.
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Não foi possível processar agora. Tente de novo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="forgot-back-to-login">
          <ArrowLeft size={16} /> Voltar ao login
        </Link>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8">
          {!sent ? (
            <>
              <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center mb-4">
                <Mail size={22} className="text-primary" />
              </div>
              <h1 className="font-heading font-black text-3xl tracking-tight">Esqueci minha senha</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Digite seu e-mail cadastrado. Enviaremos um link para redefinir sua senha.
              </p>

              <form onSubmit={submit} noValidate className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    data-testid="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="voce@exemplo.com"
                    className="h-12"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="forgot-submit"
                  disabled={loading || !email.trim()}
                  className="w-full h-12 text-base font-bold"
                >
                  {loading ? "Enviando..." : "Enviar link de redefinição"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-confirmation" className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                <Mail size={26} className="text-primary" />
              </div>
              <h1 className="font-heading font-black text-2xl tracking-tight">Verifique sua caixa de entrada</h1>
              <p className="text-sm text-muted-foreground mt-3">
                Se <strong className="text-foreground">{email}</strong> for uma conta válida,
                você receberá um e-mail com o link em alguns instantes.
                O link expira em <strong className="text-foreground">30 minutos</strong>.
              </p>
              <p className="text-xs text-muted-foreground mt-4">
                Não recebeu? Confira a caixa de spam ou tente novamente em alguns minutos.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => nav("/login")}
                data-testid="forgot-goto-login"
                className="w-full h-12 mt-6 font-bold"
              >
                Voltar ao login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
